import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import { fromLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Icon, Style } from 'ol/style';

const Geocoder = forwardRef(({ onCoordinatesChange, value }, ref) => {
  const [address, setAddress] = useState(value || {
    calle: '',
    numero: '',
    comuna: '',
    ciudad: ''
  });
  
  const [coordinates, setCoordinates] = useState(null);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Expone métodos públicos mediante ref
  useImperativeHandle(ref, () => ({
    getCoordinates: () => coordinates,
    setAddress: (newAddress) => setAddress(newAddress)
  }));

  const handleChange = (e) => {
    const newAddress = {
      ...address,
      [e.target.name]: e.target.value
    };
    setAddress(newAddress);
  };

  const geocodeAddress = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const query = `${address.calle} ${address.numero}, ${address.comuna}, ${address.ciudad}`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
      );
      
      const data = await response.json();
      
      if (data.length === 0) {
        throw new Error('Dirección no encontrada');
      }
      
      const { lat, lon } = data[0];
      const newCoordinates = [parseFloat(lon), parseFloat(lat)];
      
      setCoordinates(newCoordinates);
      if(onCoordinatesChange) onCoordinatesChange(newCoordinates);
      
      updateMap(newCoordinates);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateMap = (coords) => {
    if (map) {
      map.getView().setCenter(fromLonLat(coords));
      map.getView().setZoom(16);
      
      // Actualizar marcador
      const source = new VectorSource();
      const marker = new Feature({
        geometry: new Point(fromLonLat(coords))
      });

      marker.setStyle(new Style({
        image: new Icon({
          src: 'https://openlayers.org/en/latest/examples/data/icon.png',
          scale: 0.5
        })
      }));

      source.addFeature(marker);
      map.getLayers().getArray()
        .filter(layer => layer instanceof VectorLayer)
        .forEach(layer => layer.setSource(source));
    }
  };

  useEffect(() => {
    if (!map && coordinates) {
      const newMap = new Map({
        target: 'map',
        layers: [
          new TileLayer({
            source: new OSM()
          })
        ],
        view: new View({
          center: fromLonLat(coordinates),
          zoom: 16
        })
      });
      setMap(newMap);
    }
  }, [coordinates]);

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6 max-w-md">
        <h1 className="text-2xl font-bold mb-4">Buscador de Coordenadas</h1>
        
        <div className="space-y-4">
          <input
            type="text"
            name="calle"
            placeholder="Calle"
            className="w-full p-2 border rounded"
            value={address.calle}
            onChange={handleChange}
          />
          <input
            type="text"
            name="numero"
            placeholder="Número"
            className="w-full p-2 border rounded"
            value={address.numero}
            onChange={handleChange}
          />
          <input
            type="text"
            name="comuna"
            placeholder="Comuna"
            className="w-full p-2 border rounded"
            value={address.comuna}
            onChange={handleChange}
          />
          <input
            type="text"
            name="ciudad"
            placeholder="Ciudad"
            className="w-full p-2 border rounded"
            value={address.ciudad}
            onChange={handleChange}
          />
          
          <button
            onClick={geocodeAddress}
            disabled={loading}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:bg-gray-400"
          >
            {loading ? 'Buscando...' : 'Obtener Coordenadas'}
          </button>
        </div>

        {error && <p className="text-red-500 mt-4">{error}</p>}
      </div>

      <div id="map" className="w-full h-48 mt-8 rounded-lg shadow-lg"></div>
    </div>
  );
});

export default Geocoder;